import { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Badge,
  Label,
} from "@/components/ui";
import { PageHeader } from "@/components/ui/page-header";
import { liquidityProviderApi } from "@/services/api";
import {
  ArrowLeftRight,
  Zap,
  CheckCircle,
  XCircle,
  Loader2,
  RefreshCw,
  AlertCircle,
  DollarSign,
  Shield,
} from "lucide-react";
import { formatDate, formatCurrency } from "@/lib/utils";

interface ProviderConfig {
  id: string;
  providerCode: string;
  name: string;
  baseUrl: string;
  organizationId: string | null;
  environment: string;
  isActive: boolean | null;
  lastTestedAt: string | null;
  lastTestStatus: string | null;
  lastTestError: string | null;
  hasApiKey: boolean;
  hasApiSecret: boolean;
}

interface ProviderStatus {
  providerName: string;
  isRealProvider: boolean;
}

interface ProviderBalance {
  brl: number;
  usdc: number;
}

export function LiquidityProviderSettingsPage() {
  const [config, setConfig] = useState<ProviderConfig | null>(null);
  const [status, setStatus] = useState<ProviderStatus | null>(null);
  const [balance, setBalance] = useState<ProviderBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [loadingBalance, setLoadingBalance] = useState(false);
  const [testResult, setTestResult] = useState<{
    success: boolean;
    message: string;
  } | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    providerCode: "cainvest",
    name: "CAInvest Spot",
    apiKey: "",
    apiSecret: "",
    baseUrl: "https://api.sandbox.spot.cainvest.io",
    organizationId: "",
    environment: "sandbox" as "sandbox" | "production",
    isActive: false,
  });

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    try {
      const [configData, statusData] = await Promise.all([
        liquidityProviderApi.getConfigByCode("cainvest"),
        liquidityProviderApi.getStatus(),
      ]);

      setStatus(statusData);

      if (configData) {
        setConfig(configData);
        setFormData({
          providerCode: configData.providerCode,
          name: configData.name,
          apiKey: "",
          apiSecret: "",
          baseUrl: configData.baseUrl,
          organizationId: configData.organizationId || "",
          environment: configData.environment as "sandbox" | "production",
          isActive: configData.isActive || false,
        });

        // Se está ativo e configurado, buscar saldo
        if (configData.isActive && configData.hasApiKey) {
          loadBalance();
        }
      }
    } catch (error) {
      console.error("Erro ao carregar config:", error);
    } finally {
      setLoading(false);
    }
  }

  async function loadBalance() {
    setLoadingBalance(true);
    try {
      const balanceData = await liquidityProviderApi.getBalance("cainvest");
      setBalance(balanceData);
    } catch (error) {
      console.error("Erro ao carregar saldo:", error);
    } finally {
      setLoadingBalance(false);
    }
  }

  async function handleSave() {
    setSaving(true);
    setTestResult(null);
    try {
      const dataToSave = { ...formData };
      // Não enviar campos vazios
      if (!dataToSave.apiKey) {
        delete (dataToSave as Record<string, unknown>).apiKey;
      }
      if (!dataToSave.apiSecret) {
        delete (dataToSave as Record<string, unknown>).apiSecret;
      }

      const result = await liquidityProviderApi.upsertConfig(dataToSave);
      setConfig(result);
      setTestResult({ success: true, message: "Configuracao salva com sucesso!" });
      loadData();
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao salvar",
      });
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    setTestResult(null);
    try {
      const result = await liquidityProviderApi.testConnection("cainvest");
      setTestResult(result);
      loadData(); // Reload to get updated test status
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro no teste",
      });
    } finally {
      setTesting(false);
    }
  }

  async function handleToggleActive() {
    if (!config) return;
    setSaving(true);
    try {
      await liquidityProviderApi.setActive(config.id, !config.isActive);
      await liquidityProviderApi.reload();
      setTestResult({
        success: true,
        message: config.isActive ? "Provedor desativado" : "Provedor ativado",
      });
      loadData();
    } catch (error) {
      setTestResult({
        success: false,
        message: error instanceof Error ? error.message : "Erro ao alterar status",
      });
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
        <PageHeader
          icon={<ArrowLeftRight className="w-5 h-5 sm:w-6 sm:h-6" />}
          title="Provedor de Liquidez"
          description="Configuracao da integracao com CAInvest para conversao USDC"
        />
        <div className="flex justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
      <PageHeader
        icon={<ArrowLeftRight className="w-5 h-5 sm:w-6 sm:h-6" />}
        title="Provedor de Liquidez"
        description="Configuracao da integracao com CAInvest para conversao USDC"
      />

      {/* Status Card */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center">
                <DollarSign className="w-5 h-5" />
              </div>
              <div>
                <p className="font-medium">
                  {status?.providerName || "Nao configurado"}
                </p>
                <p className="text-sm text-muted-foreground">
                  {status?.isRealProvider ? "Provedor real (CAInvest)" : "Mock (desenvolvimento)"}
                </p>
              </div>
            </div>
            <Badge
              variant={
                config?.lastTestStatus === "success" ? "success" :
                config?.isActive ? "default" : "secondary"
              }
            >
              {config?.lastTestStatus === "success"
                ? "Conectado"
                : config?.isActive
                ? "Ativo"
                : "Inativo"}
            </Badge>
          </div>
          {config?.lastTestedAt && (
            <p className="mt-3 text-sm text-muted-foreground">
              Ultimo teste: {formatDate(config.lastTestedAt)}
            </p>
          )}
          {config?.lastTestError && (
            <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">
                {config.lastTestError}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Balance Card (se configurado e ativo) */}
      {config?.isActive && config?.hasApiKey && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg">Saldo na CAInvest</CardTitle>
            <Button
              variant="outline"
              size="sm"
              onClick={loadBalance}
              disabled={loadingBalance}
            >
              {loadingBalance ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <RefreshCw className="w-4 h-4" />
              )}
            </Button>
          </CardHeader>
          <CardContent>
            {balance ? (
              <div className="grid grid-cols-2 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold">
                    {formatCurrency(balance.brl / 100)}
                  </p>
                  <p className="text-sm text-muted-foreground">BRL</p>
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    ${(balance.usdc / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-sm text-muted-foreground">USDC</p>
                </div>
              </div>
            ) : loadingBalance ? (
              <div className="flex justify-center py-4">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                Clique em atualizar para carregar o saldo
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Credentials Config */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Credenciais API
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Ambiente</Label>
              <select
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                value={formData.environment}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    environment: e.target.value as "sandbox" | "production",
                    baseUrl: e.target.value === "production"
                      ? "https://api.spot.cainvest.io"
                      : "https://api.sandbox.spot.cainvest.io",
                  })
                }
              >
                <option value="sandbox">Sandbox (Teste)</option>
                <option value="production">Producao</option>
              </select>
            </div>
            <div>
              <Label>Base URL</Label>
              <Input
                value={formData.baseUrl}
                onChange={(e) =>
                  setFormData({ ...formData, baseUrl: e.target.value })
                }
                placeholder="https://api.sandbox.spot.cainvest.io"
              />
            </div>
          </div>

          <div>
            <Label>API Key</Label>
            <Input
              type="password"
              placeholder={config?.hasApiKey ? "Deixe vazio para manter atual" : "ak_..."}
              value={formData.apiKey}
              onChange={(e) =>
                setFormData({ ...formData, apiKey: e.target.value })
              }
            />
            {config?.hasApiKey && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> API Key configurada
              </p>
            )}
          </div>

          <div>
            <Label>API Secret</Label>
            <Input
              type="password"
              placeholder={config?.hasApiSecret ? "Deixe vazio para manter atual" : "sk_..."}
              value={formData.apiSecret}
              onChange={(e) =>
                setFormData({ ...formData, apiSecret: e.target.value })
              }
            />
            {config?.hasApiSecret && (
              <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> API Secret configurada
              </p>
            )}
          </div>

          <div>
            <Label>Organization ID (opcional)</Label>
            <Input
              placeholder="Sera preenchido automaticamente no primeiro teste"
              value={formData.organizationId}
              onChange={(e) =>
                setFormData({ ...formData, organizationId: e.target.value })
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Test Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Testar Conexao</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-3">
            <Button onClick={handleTest} disabled={testing || !config?.hasApiKey}>
              {testing ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Zap className="w-4 h-4 mr-2" />
              )}
              Testar Conexao
            </Button>

            {config && (
              <Button
                variant={config.isActive ? "destructive" : "default"}
                onClick={handleToggleActive}
                disabled={saving || !config.hasApiKey}
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                {config.isActive ? "Desativar" : "Ativar"}
              </Button>
            )}
          </div>

          {!config?.hasApiKey && (
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
              <div>
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Configure as credenciais da API antes de testar ou ativar.
                </p>
              </div>
            </div>
          )}

          {testResult && (
            <div
              className={`p-4 rounded-lg ${
                testResult.success
                  ? "bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-400"
                  : "bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400"
              }`}
            >
              <div className="flex items-center gap-3">
                {testResult.success ? (
                  <CheckCircle className="w-5 h-5" />
                ) : (
                  <XCircle className="w-5 h-5" />
                )}
                <span>{testResult.message}</span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Informacoes</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-muted-foreground">
          <p>
            <strong>CAInvest</strong> e o provedor de liquidez para conversao entre BRLD e USDC.
          </p>
          <p>
            Quando <strong>ativo</strong>, as conversoes de moeda usarao a API real da CAInvest
            com cotacoes em tempo real.
          </p>
          <p>
            Quando <strong>inativo</strong>, o sistema usa um mock com cotacao fixa de R$ 5,10
            para testes.
          </p>
          <div className="pt-3 border-t">
            <p className="text-xs">
              Documentacao API: {" "}
              <a
                href="https://api.sandbox.spot.cainvest.io/swagger/index.html"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                https://api.sandbox.spot.cainvest.io/swagger
              </a>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving} size="lg">
          {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
          Salvar Configuracao
        </Button>
      </div>
    </div>
  );
}
